import type { DeviceConfigurationApi } from '@model/DeviceConfiguration';
import type {
  InitSessionData,
  InitSessionConfig,
  InitSessionConfigStep,
  QuestionConfigStep,
  VideoConfigStep,
  ExposureConfigFromColumn,
} from '@types';
import { translateExposureConfig } from './translateExposureConfig';

const NUMERIC_KEY_REGEX = /^\d+$/;
const WAITING_ROOM_KEYS = ['returnToWaitingRoom'] as const;
type WaitingRoomConfig = {
  returnToWaitingRoom?: boolean;
};

const isRecord = (value: unknown): value is Record<string, unknown> =>
  value !== null && typeof value === 'object';

const hasWaitingRoomKeys = (value: unknown): value is WaitingRoomConfig =>
  isRecord(value) &&
  WAITING_ROOM_KEYS.some(key =>
    Object.prototype.hasOwnProperty.call(value, key)
  );

const isConfigStepCandidate = (
  value: unknown
): value is Record<string, unknown> => {
  if (!isRecord(value)) {
    return false;
  }

  if (!hasWaitingRoomKeys(value)) {
    return true;
  }

  return 'question' in value || 'video' in value || 'audio' in value;
};

/**
 * Converte i dati normalizzati in InitSessionConfigStep[]
 * Distingue tra QuestionConfigStep e VideoConfigStep
 */
const convertToInitSessionConfigSteps = (
  normalizedConfig: unknown[]
): InitSessionConfigStep[] => {
  return normalizedConfig.filter(isConfigStepCandidate).map(step => {
    // Se ha la proprietà 'question', è un QuestionConfigStep
    if ('question' in step && step.question !== undefined) {
      return {
        question: String(step.question ?? ''),
        condition: String(step.condition ?? ''),
        answers: Array.isArray(step.answers)
          ? step.answers.map(a => String(a))
          : [],
      } as QuestionConfigStep;
    }

    // Altrimenti è un VideoConfigStep
    return {
      video: String(step.video ?? ''),
      audio: String(step.audio ?? ''),
      condition: String(step.condition ?? ''),
    } as VideoConfigStep;
  });
};

export const extractWaitingRoomConfig = (
  config: DeviceConfigurationApi['config']
): WaitingRoomConfig => {
  if (config === null || typeof config === 'undefined') {
    return {};
  }

  if (Array.isArray(config)) {
    const metadata = config.find(hasWaitingRoomKeys);

    if (!metadata) {
      return {};
    }

    return {
      ...(typeof metadata.returnToWaitingRoom === 'boolean'
        ? { returnToWaitingRoom: metadata.returnToWaitingRoom }
        : {}),
    };
  }

  if (!isRecord(config)) {
    return {};
  }

  const fromNumericEntries = Object.entries(config)
    .filter(([key]) => NUMERIC_KEY_REGEX.test(key))
    .map(([, value]) => value)
    .find(hasWaitingRoomKeys);

  if (fromNumericEntries) {
    return {
      ...(typeof fromNumericEntries.returnToWaitingRoom === 'boolean'
        ? { returnToWaitingRoom: fromNumericEntries.returnToWaitingRoom }
        : {}),
    };
  }

  return {
    ...(typeof config.returnToWaitingRoom === 'boolean'
      ? { returnToWaitingRoom: config.returnToWaitingRoom }
      : {}),
  };
};

export const buildInitSessionConfigPayload = (
  config: DeviceConfigurationApi['config']
) => {
  const normalizedSteps = normalizeInitSessionConfig(config);
  const waitingRoomConfig = extractWaitingRoomConfig(config);

  return {
    ...waitingRoomConfig,
    config: normalizedSteps,
  };
};

export const normalizeInitSessionConfig = (
  config: DeviceConfigurationApi['config']
): InitSessionConfigStep[] => {
  if (config === null || typeof config === 'undefined') {
    return [];
  }

  if (Array.isArray(config)) {
    return convertToInitSessionConfigSteps(config);
  }

  if (typeof config !== 'object') {
    return [];
  }

  const legacyConfig = config as Record<string, any>;

  const stepsFromNumericKeys = Object.entries(legacyConfig)
    .filter(([key]) => NUMERIC_KEY_REGEX.test(key))
    .sort((a, b) => Number(a[0]) - Number(b[0]))
    .map(([, value]) => value)
    .filter(step => step !== undefined);

  const normalizedSteps =
    stepsFromNumericKeys.length > 0
      ? stepsFromNumericKeys
      : Array.isArray((legacyConfig as any).steps)
        ? ((legacyConfig as any).steps as any[])
        : [legacyConfig];

  return convertToInitSessionConfigSteps(normalizedSteps);
};

/**
 * Legge la config column come oggetto exposure (per sessioni type === 'exposure').
 * Se config è stringa JSON la parsa; se il parsing fallisce ritorna {}.
 * Passa da `translateExposureConfig` (legacy foodIds/buffet → itemIds/isMultiItem).
 */
export function getExposureConfigFromSession(
  session: DeviceConfigurationApi
): ExposureConfigFromColumn {
  return translateExposureConfig(session?.config);
}

type WithConfigEnvelope<T extends { config?: any }> = Omit<T, 'config'> & {
  config: {
    config: InitSessionConfigStep[];
  };
};
//TODO: TEMP

export const ensureConfigEnvelope = <T extends { config?: any }>(
  payload: T
): WithConfigEnvelope<T> => {
  if (
    payload &&
    typeof payload === 'object' &&
    payload.config &&
    typeof payload.config === 'object' &&
    'config' in payload.config
  ) {
    return payload as WithConfigEnvelope<T>;
  }

  return {
    ...(payload as Omit<T, 'config'>),
    config: {
      config: Array.isArray(payload?.config)
        ? payload.config
        : (payload?.config?.config ?? []),
    },
  };
};

export const extractSessionObject = (
  currentExperience: DeviceConfigurationApi
): InitSessionData['config'] | undefined => {
  if (!currentExperience) {
    return undefined;
  }

  return {
    id: currentExperience.id,
    status: 'queued',
    config: JSON.stringify(
      normalizeInitSessionConfig(currentExperience.config)
    ),
    scenario: currentExperience.scenario,
    device_id: parseInt(currentExperience.device_id),
    patient_id: currentExperience.patient_id,
    type: currentExperience.type,
    patientCode: currentExperience.patientCode,
    deviceName: currentExperience.deviceName,
    deviceCode: currentExperience.deviceCode,
    connectedTo: currentExperience.connectedTo || '',
    hrData: currentExperience.hrData,
    results: currentExperience.results,
    baseline: currentExperience.baseline,
    connectedDevices: currentExperience.connectedDevices,
    video: currentExperience.video || {
      id: 0,
      name: '',
      version: 0,
      url: '',
      category: '',
      size: 0,
      duration: 0,
      type: '',
      product_name: '',
      product_image: '',
      product_description: '',
    },
  };
};

export const buildLegacyInitSessionConfig = (
  currentExperience: DeviceConfigurationApi
): InitSessionConfig => {
  const legacyConfig = extractSessionObject(currentExperience);
  const waitingRoomConfig = extractWaitingRoomConfig(currentExperience.config);

  if (!legacyConfig) {
    throw new Error('Current experience is required');
  }

  return {
    ...legacyConfig,
    ...waitingRoomConfig,
  };
};
